"use client";

import { createContext, useContext, useState, useEffect, useCallback, FC, ReactNode } from "react";
import type {
  GlobalMetrics,
  PoolRiskApiResponseObject,
  Market,
  Pair,
  Token,
  AssetDetails,
  TransformedCandleData,
} from "@/utils/types";
import { AllowedPeriods } from "@/utils/utilities";

// Import everything from data.service.ts:
import {
  fetchCoreData,
  fetchPeriodData,
  fetchCandles as serviceFetchCandles,
  fetchTokenDetails as serviceFetchTokenDetails,
  getPairsForToken as serviceGetPairsForToken,
  buildPoolRoute as serviceBuildPoolRoute,
} from "@/services/data.service";

interface PeriodSnapshot {
  globalMetrics: GlobalMetrics | null;
  poolRiskData: PoolRiskApiResponseObject[];
};

export interface DataContextValue {
  loading: boolean;
  globalMetrics: GlobalMetrics | null;
  poolRiskData: PoolRiskApiResponseObject[];
  markets: Market[];
  pairs: Pair[];
  tokens: Token[];
  period: AllowedPeriods;
  setPeriod: (p: AllowedPeriods) => void;
  getPeriodData: (p: AllowedPeriods) => Promise<PeriodSnapshot>;
  fetchCandles: (
    token0: string,
    token1: string | null,
    from: number,
    to: number,
  ) => Promise<TransformedCandleData[]>;
  fetchTokenDetails: (asset: string) => Promise<AssetDetails | null>;
  getPairsForToken: (token: Token) => Pair[];
  buildPoolRoute: (pool: PoolRiskApiResponseObject) => string;
}

const DataContext = createContext<DataContextValue | undefined>(undefined);

export const DataProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [globalMetrics, setGlobalMetrics] = useState<GlobalMetrics | null>(null);
  const [poolRiskData, setPoolRiskData] = useState<PoolRiskApiResponseObject[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [pairs, setPairs] = useState<Pair[]>([]);
  const [tokens, setTokens] = useState<Token[]>([]);
  const [period, setPeriodState] = useState<AllowedPeriods>("360d"); // default

  /**
   * Load snapshots of the core data on mount.
   */
  const [periodCache, setPeriodCache] = useState<Partial<Record<AllowedPeriods, PeriodSnapshot>>>({});

  const processCoreData = useCallback(async () => {
    setLoading(true);
    try {
      const { markets, pairs, tokens } = await fetchCoreData();
      setMarkets(markets);
      setPairs(pairs);
      setTokens(tokens);
    } catch (error) {
      console.error("Error loading core data:", error);
      setMarkets([]);
      setPairs([]);
      setTokens([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch data that depends on the 'period' (like metrics, pool stats, etc.).
   */
  const processPeriodData = useCallback(async (p: AllowedPeriods) => {
    try {
      const { globalMetrics, poolRiskData } = await fetchPeriodData(p);
      setGlobalMetrics(globalMetrics);
      setPoolRiskData(poolRiskData);
      setPeriodCache((prev) => ({
        ...prev,
        [p]: { globalMetrics, poolRiskData },
      }));
    } catch (error) {
      console.error("Error fetching period-based data:", error);
      setGlobalMetrics(null);
      setPoolRiskData([]);
    }
  }, []);

  useEffect(() => {
    void processCoreData();
  }, [processCoreData]);

  useEffect(() => {
    if (!loading && tokens.length > 0 && pairs.length > 0 && markets.length > 0) {
      void processPeriodData(period);
    }
  }, [period, processPeriodData, loading, tokens.length, pairs.length, markets.length]);

  const setPeriod = (p: AllowedPeriods) => { setPeriodState(p); };

  // ADDED: on-demand getter that DOES NOT change global period; it caches results per period
  const getPeriodData = useCallback(
    async (p: AllowedPeriods): Promise<PeriodSnapshot> => {
      const cached = periodCache[p];
      if (cached) return cached;

      const { globalMetrics, poolRiskData } = await fetchPeriodData(p);
      const snap = { globalMetrics, poolRiskData };
      setPeriodCache((prev) => ({ ...prev, [p]: snap }));
      return snap;
    },
    [periodCache],
  );

  const fetchCandles = async (token0: string, token1: string | null, from: number, to: number) =>
    serviceFetchCandles(token0, token1, from, to);

  const fetchTokenDetails = async (asset: string) => serviceFetchTokenDetails(asset);

  const getPairsForToken = (token: Token) => serviceGetPairsForToken(token, pairs);

  const buildPoolRoute = (pool: PoolRiskApiResponseObject) => serviceBuildPoolRoute(pool, pairs, tokens, period);

  const value: DataContextValue = {
    loading,
    globalMetrics,
    poolRiskData,
    markets,
    pairs,
    tokens,
    period,
    setPeriod,
    getPeriodData, // ADDED
    fetchCandles,
    fetchTokenDetails,
    getPairsForToken,
    buildPoolRoute,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export function useDataContext() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useDataContext must be used within DataProvider");
  }
  return context;
}
